import { ChatStatus, IChatMessage, IChatState } from './chatbot.types';
import { textPart } from './chatbot.helpers';

export type ChatAction =
  | { type: 'send/start'; userMessage: IChatMessage; assistantId: string }
  | { type: 'send/chunk'; assistantId: string; cumulativeText: string }
  | { type: 'send/done'; assistantId: string; finalText: string }
  | { type: 'send/error'; assistantId: string; error: string }
  | { type: 'send/abort'; assistantId: string }
  | { type: 'reset' };

export const initialChatState: IChatState = {
  messages: [],
  status: 'idle',
  error: null,
};

// Replace the assistant message's text. Single-text-content messages only, so we
// rewrite the content array each render rather than splicing parts.
const updateAssistantText = (
  messages: readonly IChatMessage[],
  assistantId: string,
  text: string
): IChatMessage[] =>
  messages.map((m) =>
    m.id === assistantId ? { ...m, content: [textPart(text)] } : m
  );

export const chatReducer = (state: IChatState, action: ChatAction): IChatState => {
  switch (action.type) {
    case 'send/start': {
      const placeholder: IChatMessage = {
        id: action.assistantId,
        role: 'assistant',
        content: [textPart('')],
      };
      return {
        messages: [...state.messages, action.userMessage, placeholder],
        status: 'streaming' as ChatStatus,
        error: null,
      };
    }
    case 'send/chunk': {
      return {
        ...state,
        messages: updateAssistantText(state.messages, action.assistantId, action.cumulativeText),
      };
    }
    case 'send/done': {
      return {
        messages: updateAssistantText(state.messages, action.assistantId, action.finalText),
        status: 'idle',
        error: null,
      };
    }
    case 'send/error': {
      // Keep partial text the user saw; if the stream produced nothing, stub it so
      // an empty bubble next to the error banner doesn't read as a UI bug.
      const messages = state.messages.map((m) => {
        if (m.id !== action.assistantId) return m;
        const text = m.content.map((p) => (p.type === 'text' ? p.text : '')).join('');
        return text ? m : { ...m, content: [textPart('(no response)')] };
      });
      return { messages, status: 'error', error: action.error };
    }
    case 'send/abort': {
      // Drop the placeholder assistant message entirely — the user cancelled
      // before any tokens streamed.
      const text = state.messages
        .find((m) => m.id === action.assistantId)
        ?.content.map((p) => (p.type === 'text' ? p.text : ''))
        .join('') || '';
      const messages = text
        ? state.messages
        : state.messages.filter((m) => m.id !== action.assistantId);
      return { messages, status: 'idle', error: null };
    }
    case 'reset': {
      return initialChatState;
    }
    default: {
      // Exhaustiveness check — a new unhandled action type fails to typecheck.
      // Unreachable at runtime today.
      const _exhaustive: never = action;
      return state;
    }
  }
};
